import { z } from 'zod';
import type { PlanModule } from '../constants/plans.js';

/**
 * The first fifteen minutes after signup, described once.
 *
 * A trial account that still looks empty on day three has already churned, so the whole point
 * of this list is to get a shop to a REAL booked order before it closes the tab. Two rules
 * follow from that and both are encoded here rather than left to the UI:
 *
 *  - every step is skippable, and skipping is a first-class outcome rather than an escape
 *    hatch. A shop with no GST registration must be able to say so and move on, and a shop
 *    that does not use WhatsApp yet is not broken;
 *  - no step is a gate. Setup is never a precondition for booking an order — the product must
 *    work half-configured, because the owner will be interrupted by an actual customer in the
 *    middle of it, and a wizard that has to be finished before the shop can serve that
 *    customer is a wizard that gets abandoned along with the trial.
 *
 * `satisfiedBy` is what keeps the checklist honest. A shop that invited a karigar from the
 * staff screen has done that step, whether or not it ever opened the wizard, and a checklist
 * that still nags them for it is one they stop believing.
 */

export const ONBOARDING_STEPS = [
  'shop_details',
  'gst',
  'stages',
  'garment_types',
  'first_staff',
  'whatsapp',
] as const;
export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number];
export const onboardingStepKeySchema = z.enum(ONBOARDING_STEPS);

/** Where a step stands. `pending` is the only one that still asks anything of the owner. */
export const ONBOARDING_STEP_STATUSES = ['pending', 'completed', 'skipped'] as const;
export type OnboardingStepStatus = (typeof ONBOARDING_STEP_STATUSES)[number];
export const onboardingStepStatusSchema = z.enum(ONBOARDING_STEP_STATUSES);

/** The counted facts about a shop the checklist reads itself out of. */
export interface OnboardingCounts {
  readonly customers: number;
  readonly staff: number;
  readonly orders: number;
  readonly measurementTemplates: number;
  readonly whatsappConnected: boolean;
  readonly gstAnswered: boolean;
  readonly shopDetailsFilled: boolean;
}

export interface OnboardingStepDefinition {
  readonly key: OnboardingStepKey;
  /** English name; the UI resolves `onboarding.step.<key>.title` through i18next. */
  readonly defaultTitle: string;
  /** The route the wizard lands on when the owner picks this step up again. */
  readonly path: string;
  /** Set when the step configures a module a cheaper plan does not include. */
  readonly module?: PlanModule;
  /** True when the shop has already done this, wizard or not. */
  readonly satisfiedBy: (counts: OnboardingCounts) => boolean;
}

export const ONBOARDING_STEP_DEFINITIONS: readonly OnboardingStepDefinition[] = Object.freeze([
  {
    key: 'shop_details',
    defaultTitle: 'Your shop',
    path: '/settings/shop',
    satisfiedBy: (counts) => counts.shopDetailsFilled,
  },
  {
    key: 'gst',
    defaultTitle: 'GST registration',
    path: '/settings/shop#gst',
    satisfiedBy: (counts) => counts.gstAnswered,
  },
  {
    key: 'stages',
    defaultTitle: 'Your workflow',
    path: '/settings/workflow',
    // Every shop is born with the canonical stages, so this step is about CHOOSING them.
    // Nothing counted can prove that choice was made, which is why it has no auto-completion.
    satisfiedBy: () => false,
  },
  {
    key: 'garment_types',
    defaultTitle: 'What you stitch',
    path: '/settings/measurements',
    satisfiedBy: (counts) => counts.measurementTemplates > 0 && counts.orders > 0,
  },
  {
    key: 'first_staff',
    defaultTitle: 'Your first karigar',
    path: '/staff',
    // The owner is a seat too, so "more than one" is the test for having added somebody.
    satisfiedBy: (counts) => counts.staff > 1,
  },
  {
    key: 'whatsapp',
    defaultTitle: 'WhatsApp updates',
    // Integrations, not notifications: the step is done the moment the workspace is connected,
    // and connecting is what the integrations screen is for. What the shop then SAYS to its
    // customers is a longer conversation that lives on the notifications screen.
    path: '/settings/integrations',
    module: 'relay_whatsapp',
    satisfiedBy: (counts) => counts.whatsappConnected,
  },
]);

export function onboardingStepDefinition(key: OnboardingStepKey): OnboardingStepDefinition {
  const definition = ONBOARDING_STEP_DEFINITIONS.find((step) => step.key === key);
  if (!definition) throw new Error(`Unknown onboarding step: ${key}`);
  return definition;
}

/** One step as the checklist renders it. */
export interface OnboardingStepView {
  readonly key: OnboardingStepKey;
  readonly status: OnboardingStepStatus;
  readonly completedAt: string | null;
  /**
   * True when the status came from what the shop has actually done rather than from a button
   * on the wizard — so the UI can say "already done" instead of taking credit for it.
   */
  readonly autoCompleted: boolean;
  /** False when the shop's plan does not include the module this step configures. */
  readonly available: boolean;
}

/**
 * How far along a shop is.
 *
 * Skipped steps count towards being FINISHED but not towards the progress bar: a shop that
 * skipped four of six has finished setting up, and telling them they are at 33% would be both
 * discouraging and untrue. What the bar measures is what has been set up; what `finished`
 * measures is whether the product should still be asking.
 */
export interface OnboardingProgressSummary {
  readonly total: number;
  readonly completed: number;
  readonly skipped: number;
  readonly pending: number;
  readonly percentComplete: number;
  readonly finished: boolean;
}

export function summariseOnboarding(
  steps: readonly OnboardingStepView[],
): OnboardingProgressSummary {
  const available = steps.filter((step) => step.available);
  const total = available.length;
  const completed = available.filter((step) => step.status === 'completed').length;
  const skipped = available.filter((step) => step.status === 'skipped').length;
  const pending = total - completed - skipped;
  return {
    total,
    completed,
    skipped,
    pending,
    percentComplete: total === 0 ? 100 : Math.round((completed / total) * 100),
    finished: pending === 0,
  };
}
